/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Box, HStack, Input, Select, Text, createListCollection, Portal } from '@chakra-ui/react';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import { MAPPING_HELP } from './mapping-help';
import {
  renderForWire,
  surfaceCarriesStructure,
  type WireRendering,
  type WireRenderResult,
  type WireSurfaceClass,
} from '@wire-rendering';
import type { DestinationFieldType } from '@destination-field';

/**
 * **Send structured values as** — how an object or a nested list goes out on a surface that
 * cannot carry structure (D23 · R3, #1083).
 *
 * **The name scopes it, because the prose could not** (2026-08-20). It was "Send as", which
 * is true of every value and therefore said nothing about the one case it governs — so it
 * needed a standing line of prose to take the scope back, directly under an option called
 * *Flatten it to text*, and the pair read as a contradiction. The line is gone and the
 * label does the work: #1045's *state the outcome* applied to a control's own name.
 *
 * **Rendered by {@link MappingControl}, never beside it** (2026-08-20). It shipped as a
 * sibling of the mapping and landed three lines under a *different* select also called
 * **Send as** — #923's scalar-type coercion — so one row asked the same-sounding question
 * twice about one value. Todd, on seeing it: *"why should we have both? It seems
 * unnecessary to separate them."* There is one **Send as** per row now, and what it offers
 * is what that surface can carry **and store**: the type half is inert and unstorable on a
 * query string, so it is not offered there at all rather than merged in as four dead
 * options.
 *
 * The vocabulary and the rendering both come from `@wire-rendering`, the module the driver
 * runs. That is the point of aliasing it rather than mirroring: this control previews with
 * **the same function that will send**, so the example cannot drift from the wire. A
 * preview computed by a second implementation would read as proof of something we do not
 * do (#1002).
 *
 * Three things this control has to say that a bare dropdown would not:
 *
 * - **It is inert for values the surface can already carry.** `renderForWire` returns a
 *   scalar — and a flat list — untouched whatever the rendering says. **Shown, not
 *   claimed**: the two example lines render an object and a plain list under every option,
 *   and the list line never moves. Left unsaid, the operator reads the selection as a
 *   transform applied to every value and then cannot explain the request; *said* in prose,
 *   it contradicted the option sitting above it.
 * - **Unset and "never send" are different**, and both are reachable. Unset means *no
 *   rendering was authored*: the value is dropped **and reported**, because nobody decided.
 *   `omit` is a decision and is therefore silent. Collapsing them would either hide a real
 *   defect or train the operator to ignore a warning they chose.
 * - **What it will actually look like.** The example is rendered live through
 *   `renderForWire`, which is what makes `join` authorable at all: separator, pair
 *   separator and key order are three knobs whose product is a string nobody can predict
 *   from the words.
 */

/**
 * Two examples, and the second one is the whole point.
 *
 * A rendering is **inert for anything the surface can already carry** — a scalar and a flat
 * list ride untouched, because comma-joining a list is the IAB URL convention and needed no
 * author. That used to be asserted in a standing line of prose *("only applies when the
 * value has structure — a plain value, or a list of them, is sent as it is")* which sat
 * directly under an option called **Flatten it to text**, and read as a contradiction:
 * reported 2026-08-20, *"it's all very confusing."*
 *
 * So the list is **shown** rather than claimed, rendered through the same `renderForWire`
 * the driver runs. Under every option the object line moves and the list line does not,
 * which is a thing an operator can see in one glance and cannot misread.
 */
const EXAMPLE_OBJECT = { a: 1, b: 2 } as const;
const EXAMPLE_OBJECT_LABEL = '{ a: 1, b: 2 }';

/**
 * **On a JSON leaf that takes one value, the example is a list** (`4f`, `#1416`). A body leaf can be
 * handed a flat list and nothing more structured (the engine refuses an object from a path), and every
 * option applies to it there, where on a query string a flat list rides untouched. So the line the
 * operator watches is the value this control exists for on that surface.
 */
const EXAMPLE_LIST = ['Thriller', 'Drama'] as const;
const EXAMPLE_LIST_LABEL = '["Thriller", "Drama"]';

type SendAsKind = 'unset' | 'omit' | 'json' | 'object' | 'join';

function kindOf(rendering: WireRendering | undefined): SendAsKind {
  if (rendering === undefined) return 'unset';
  if (rendering === 'omit' || rendering === 'json' || rendering === 'object') return rendering;
  return 'join';
}

/**
 * Labels name the **outcome**, not the mechanism (#1045). "Omit" is our word for it; what
 * the operator needs to know is whether the value goes out, and whether they will be told
 * when it does not.
 */
/**
 * **Unset, where unset still sends something** (`4f`). On a JSON leaf that takes one value, a list
 * nobody rendered goes out joined with commas, as that leaf always sent it — so *drop it and tell me*
 * would describe a different surface's outcome (#1045). The label is chosen by rendering the example
 * with nothing authored, so it follows `renderForWire` rather than restating it.
 */
const UNSET_JOINED = {
  label: 'Not set — separate with commas',
  hint: 'Each member is sent, with a comma between them.',
};

const KIND_LABELS: Record<SendAsKind, { label: string; hint: string }> = {
  unset: {
    label: 'Not set — drop it and tell me',
    hint: 'Left out of the request and reported as unsent.',
  },
  omit: {
    // **Renamed 2026-08-27.** It was *"Never send it"*, and `P1` slice 3 put a **Never send**
    // checkbox on the same row — one governing the whole mapping, this one governing only a value
    // with structure the surface cannot carry. Two near-identical labels at two scopes on one row
    // is #1045's *never reuse a name the UI already gives to something else*, and the narrower of
    // the two is the one that has to say so.
    label: 'Always leave it out',
    hint: 'Left out, silently — you have decided, so there is nothing to report.',
  },
  json: {
    label: 'Send it as JSON text',
    hint: 'Escaping, nesting and key order are all fixed by the format, so there is nothing further to choose.',
  },
  object: {
    label: 'Send it as-is',
    hint: 'Goes out with its structure intact. Only possible where the surface carries structure.',
  },
  join: {
    label: 'Flatten it to text',
    hint: 'One level only. A value nesting below that is left out and reported, rather than flattened into something the receiver cannot parse.',
  },
};

/**
 * One style for all four labels, each sitting **on the same line as the control it names**.
 *
 * This is the second report against one invariant, and they ask for the same thing. On
 * 2026-08-20 the group label sat *above* the row while the three join labels sat inside it, so
 * the controls stepped down the screen (*"the row of controls is not aligned"*). On 2026-08-27
 * the labels themselves came off their controls' line — and the row's *other* select,
 * `ValueTypeControl`'s **Send as**, has always been `label · control` inline, so one row was
 * rendering the same kind of setting two ways. Keep all four together when adding a fifth.
 */
const KNOB_LABEL = { fontSize: 'xs', fontWeight: '600', color: 'fg.muted', whiteSpace: 'nowrap' } as const;

export interface SendAsControlProps {
  value: WireRendering | undefined;
  onChange: (next: WireRendering | undefined) => void;
  /** What this surface can carry. */
  surface: WireSurfaceClass;
  /**
   * **What the row sends** (`sentType`), which with `surface` is what `renderForWire` keys on (`4f`).
   * Required and nullable: `null` says nothing declares one. It decides whether `object` is offered —
   * only where the row sends a structure — and which example the line renders.
   */
  sends: DestinationFieldType | null;
  disabled?: boolean;
}

export const SendAsControl: React.FC<SendAsControlProps> = ({ value, onChange, surface, sends, disabled = false }) => {
  const kind = kindOf(value);
  const join = typeof value === 'object' ? value : undefined;

  // `object` is not offered where it cannot work. `renderForWire` reports it as
  // `not-representable-here` rather than stringifying, so offering it would be offering a
  // validation finding — the same reason a preset ships a blank endpoint rather than a
  // guessed host. It works only where the row sends a structure (`4f`): a JSON leaf that takes
  // one value refuses it as a query string does.
  const takesStructure = surfaceCarriesStructure(surface) && (sends === 'array' || sends === 'object');
  const kinds: SendAsKind[] = takesStructure
    ? ['unset', 'omit', 'json', 'object', 'join']
    : ['unset', 'omit', 'json', 'join'];

  // A text surface is shown an object, since a flat list rides there untouched; a JSON leaf is shown
  // the list, the one structure it can be handed.
  const listExample = surfaceCarriesStructure(surface) && !takesStructure;
  const example = listExample ? EXAMPLE_LIST : EXAMPLE_OBJECT;
  const exampleLabel = listExample ? EXAMPLE_LIST_LABEL : EXAMPLE_OBJECT_LABEL;
  const unset =
    renderForWire(example, undefined, surface, sends).value !== undefined ? UNSET_JOINED : KIND_LABELS.unset;
  const labelFor = (k: SendAsKind) => (k === 'unset' ? unset : KIND_LABELS[k]);

  const collection = React.useMemo(
    () => createListCollection({ items: kinds.map((k) => ({ label: labelFor(k).label, value: k })) }),
    // `kinds` and the unset label are derived from `surface` and `sends` alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surface, sends],
  );

  const setKind = (next: SendAsKind) => {
    if (next === 'unset') return onChange(undefined);
    if (next === 'join') return onChange({ kind: 'join', separator: ',' });
    onChange(next);
  };

  const patchJoin = (patch: Partial<Extract<WireRendering, { kind: 'join' }>>) => {
    onChange({ kind: 'join', separator: ',', ...join, ...patch });
  };

  // Rendered by the function that sends. `join` with an empty separator is a legal string
  // and previews as one; nothing here second-guesses the author.
  const rendered = renderForWire(example, value, surface, sends);

  return (
    <Box>
      {/* Four knobs, each `label · control` on one line — see {@link KNOB_LABEL} for why that
          is an invariant rather than a style choice. The group wraps rather than compressing,
          because `join` turns one control into four and a squeezed separator box is unreadable
          at the width that matters. */}
      <HStack gap={4} align="center" flexWrap="wrap">
        <HStack gap={2} align="center" flex="1" minW="260px" maxW="440px">
          {/* **Named for what it governs, not for what it is.** "Send as" was true of every
              value and therefore said nothing about the one case it applies to — and it
              collided with #923's scalar type control. Todd: *"maybe the send as could say
              'Send Lists or nested values as' — or 'send structured values as'."* The
              second, because a *flat* list is carried natively and this never touches one;
              naming lists here would promise a transform that does not happen. */}
          <Text {...KNOB_LABEL}>Send structured values as</Text>
          {/* The `?` joins the label because every knob on this panel carries one (#1179) — a help
              affordance on some controls and not others reads as *those are the confusing ones*. */}
          {/* Each option's sentence lives here, not under the select (Todd, 2026-09-15): the live
              example below already shows what the chosen option does, so prose beside it only
              restates it. Only the options this surface offers are described — naming *Send it
              as-is* where it cannot be chosen would be a claim about a control that is not there. */}
          <FieldHelpTooltip
            ariaLabel="About Send structured values as"
            content={
              <>
                {MAPPING_HELP.sendStructuredAs}
                {collection.items.map((item) => (
                  <React.Fragment key={item.value}>
                    <br />
                    <br />
                    <strong>{item.label}</strong> — {labelFor(item.value as SendAsKind).hint}
                  </React.Fragment>
                ))}
              </>
            }
          />
          <Box flex="1" minW="180px">
            <Select.Root
              size="sm"
              disabled={disabled}
              value={[kind]}
              onValueChange={(d) => setKind(d.value[0] as SendAsKind)}
              collection={collection}
            >
              <Select.Trigger bg="bg.card" borderColor="border.default">
                <Select.ValueText />
              </Select.Trigger>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {collection.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        {item.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          </Box>
        </HStack>

        {kind === 'join' && (
          <>
            {/* The two separators are named by **where they sit**, which is the only thing
                that distinguishes them. "Between values" and "Key to value" described the
                same relationship twice from different angles and had to be worked out from
                the example. */}
            <HStack gap={2} align="center">
              <Text {...KNOB_LABEL}>Between entries</Text>
              <Input
                size="sm"
                bg="bg.card"
                width="4.5rem"
                value={join?.separator ?? ','}
                onChange={(e) => patchJoin({ separator: e.target.value })}
                disabled={disabled}
                fontFamily="mono"
                borderColor="border.default"
              />
            </HStack>
            <HStack gap={2} align="center">
              {/* Empty means values only, which is why the placeholder says so rather than
                  showing a plausible default the author never chose. */}
              <Text {...KNOB_LABEL}>Between key and value</Text>
              <Input
                size="sm"
                bg="bg.card"
                width="7rem"
                value={join?.pairWith ?? ''}
                onChange={(e) => patchJoin({ pairWith: e.target.value === '' ? undefined : e.target.value })}
                placeholder="values only"
                disabled={disabled}
                fontFamily="mono"
                borderColor="border.default"
              />
            </HStack>
            <HStack gap={2} align="center">
              <Text {...KNOB_LABEL}>Key order</Text>
              <Box minW="130px">
                <Select.Root
                  size="sm"
                  disabled={disabled}
                  value={[join?.order ?? 'declared']}
                  onValueChange={(d) => patchJoin({ order: d.value[0] as 'declared' | 'alphabetical' })}
                  collection={ORDER_COLLECTION}
                >
                  <Select.Trigger bg="bg.card" borderColor="border.default">
                    <Select.ValueText />
                  </Select.Trigger>
                  <Portal>
                    <Select.Positioner>
                      <Select.Content>
                        {ORDER_COLLECTION.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            {item.label}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Portal>
                </Select.Root>
              </Box>
            </HStack>
          </>
        )}
      </HStack>

      {/* **One example, and it is the live one** (`#1179`, 2026-09-04).
          There were two, and they are not the same kind of thing: the object line **recomputes** from
          the option, the separator and the order, while the plain-list line **never moves** — the
          comment this replaces said so. So the constant one is teaching text and moved behind the `?`
          (category 1); the live one is *shown, not explained* (category 2) and stays, because putting
          per-option feedback behind a hover removes the thing it exists to give.
          **And the option's sentence followed it behind the `?`** (Todd, 2026-09-15): the example
          already says what the chosen option does — *not sent, and reported* — so a sentence beside it
          restated the example. The example is the only line left under the control. */}
      <ExampleLine label={exampleLabel} result={rendered} />
    </Box>
  );
};

const ExampleLine: React.FC<{ label: string; result: WireRenderResult; note?: string }> = ({ label, result, note }) => (
  <Text fontSize="xs" color="fg.muted" mt={1} fontFamily="mono">
    {label} →{' '}
    {result.value !== undefined ? (
      <Text as="span" color="fg.default">
        {typeof result.value === 'object' ? JSON.stringify(result.value) : String(result.value)}
      </Text>
    ) : (
      <Text as="span" color={result.omission === 'authored' ? 'fg.muted' : 'warning.solid'}>
        {result.omission === 'authored' ? 'not sent' : 'not sent, and reported'}
      </Text>
    )}
    {note && (
      <Text as="span" fontFamily="body" color="fg.muted">
        {'  — '}
        {note}
      </Text>
    )}
  </Text>
);

const ORDER_COLLECTION = createListCollection({
  items: [
    { label: 'As given', value: 'declared' },
    { label: 'Alphabetical', value: 'alphabetical' },
  ],
});
